import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { css } from '@emotion/react';
import {
  TextButton,
  Button,
  IconButton,
  IconSearch,
  IconChevronDown,
  IconPlus,
  IconChart,
  IconToggle,
  IconToggleGroup,
  Badge,
  Modal,
} from '@flodesk/grain';
import { ROUTES } from '@/config/routes';
import Header from '@/components/app/Header';
import email1 from '@/assets/emails/email-1.png';
import email2 from '@/assets/emails/email-2.png';
import email3 from '@/assets/emails/email-3.png';
import email4 from '@/assets/emails/email-4.png';
import email5 from '@/assets/emails/email-5.png';
import email6 from '@/assets/emails/email-6.png';

function IconSquare() {
  return (
    <svg width="16" height="18" viewBox="0 0 32 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.498 34A4.5 4.5 0 0 1 0 29.5v-23A4.5 4.5 0 0 1 4.5 2h23A4.5 4.5 0 0 1 32 6.5v23c0 2.484-2.01 4.5-4.497 4.5H4.498M3 29.5c0 .83.671 1.5 1.498 1.5h23.005C28.33 31 29 30.33 29 29.5v-23A1.5 1.5 0 0 0 27.5 5h-23A1.5 1.5 0 0 0 3 6.5z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconListView() {
  return (
    <svg width="17" height="18" viewBox="0 0 34 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 7.5A1.5 1.5 0 0 1 1.5 6h31a1.5 1.5 0 0 1 0 3h-31A1.5 1.5 0 0 1 0 7.5M0 18a1.5 1.5 0 0 1 1.5-1.5h31a1.5 1.5 0 0 1 0 3h-31A1.5 1.5 0 0 1 0 18m1.5 9a1.5 1.5 0 0 0 0 3h31a1.5 1.5 0 0 0 0-3z"
      />
    </svg>
  );
}

function IconCalendar() {
  return (
    <svg width="16" height="18" viewBox="0 0 32 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fill="currentColor"
        d="M22.5.5A1.5 1.5 0 0 1 24 2v3h3.5A4.5 4.5 0 0 1 32 9.5v19.944a4.5 4.5 0 0 1-4.49 4.5l-23 .05a4.5 4.5 0 0 1-4.51-4.5V9.5A4.5 4.5 0 0 1 4.5 5H8V2a1.5 1.5 0 1 1 3 0v3h10V2A1.5 1.5 0 0 1 22.5.5M3 29.493a1.5 1.5 0 0 0 1.503 1.5l23-.049a1.5 1.5 0 0 0 1.497-1.5V16H3zM4.5 8A1.5 1.5 0 0 0 3 9.5V13h26V9.5A1.5 1.5 0 0 0 27.5 8z"
      />
    </svg>
  );
}

const pageStyles = css`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const contentStyles = css`
  flex: 1;
  background: var(--grn-color-background2);
  padding: 56px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
`;

const headerRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 1140px;
`;

const tabsStyles = css`
  display: flex;
  align-items: center;
  gap: 24px;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 24px;
  line-height: 28.6px;
  white-space: nowrap;
`;

const tabActiveStyles = css`
  color: var(--grn-color-content);
`;

const tabInactiveStyles = css`
  color: var(--grn-color-content2);
  cursor: pointer;

  &:hover {
    color: var(--grn-color-content);
  }
`;

const filtersStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const searchSortStatusStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const gridStyles = css`
  display: flex;
  flex-wrap: wrap;
  gap: 56px;
  width: 1140px;
`;

const cardWrapperStyles = css`
  width: 342px;
  background: var(--grn-color-background);
  border: 1px solid var(--grn-color-border);
  border-radius: var(--grn-radius-l);
  overflow: hidden;
  box-shadow:
    0px 1px 1px -0.1px rgba(0, 0, 0, 0.05),
    0px 1.98px 3.06px -0.36px rgba(0, 0, 0, 0.05);
`;

const coverStyles = css`
  width: 100%;
  height: 339px;
  object-fit: cover;
  display: block;
`;

const cardBodyStyles = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 32px 32px;
`;

const cardTitleStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
`;

const badgeRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 28px;
`;

type EmailCard = {
  title: string;
  image: string;
  status: 'draft' | 'scheduled' | 'sent';
  meta: string;
};

const EMAILS: EmailCard[] = [
  {
    title: 'Personal welcome message',
    image: email1,
    status: 'draft',
    meta: 'Draft last edited 2 hours ago',
  },
  {
    title: 'Personal welcome message',
    image: email2,
    status: 'scheduled',
    meta: 'Scheduled for Jan 26 at 12pm',
  },
  {
    title: 'Personal welcome message',
    image: email3,
    status: 'sent',
    meta: 'Sent to 11 on Mar 19',
  },
  {
    title: 'Personal welcome message',
    image: email4,
    status: 'draft',
    meta: 'Draft last edited yesterday',
  },
  {
    title: 'Personal welcome message',
    image: email5,
    status: 'scheduled',
    meta: 'Scheduled for Feb 14 at 9am',
  },
  {
    title: 'Personal welcome message',
    image: email6,
    status: 'sent',
    meta: 'Sent to 204 on Mar 12',
  },
];

function StatusBadge({ status, meta }: { status: EmailCard['status']; meta: string }) {
  if (status === 'draft') {
    return (
      <Badge backgroundColor="transparent" borderColor="border2" color="content2">
        {meta}
      </Badge>
    );
  }
  if (status === 'scheduled') {
    return (
      <Badge backgroundColor="#e0e7c6" color="content">
        {meta}
      </Badge>
    );
  }
  return (
    <Badge backgroundColor="shade12" color="shade1">
      {meta}
    </Badge>
  );
}

export default function Emails() {
  const history = useHistory();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalShown, setModalShown] = useState(false);
  const [modalVariant, setModalVariant] = useState<'initial' | 'followup'>('initial');
  const modalFocusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (modalShown) return undefined;
    const timer = setTimeout(() => {
      setModalOpen(true);
      setModalShown(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, [modalShown]);

  const handleRemindLater = () => setModalVariant('followup');
  const handleLearnMore = () => setModalVariant('initial');
  const handleReview = () => {
    setModalOpen(false);
    history.push(ROUTES.subscribers, { openSegmentDays: 90 });
  };

  return (
    <div css={pageStyles} className="grn-context">
      <Header activeNav="Emails" logoVariant="flodesk" />
      <div css={contentStyles}>
        <div css={headerRowStyles}>
          <div css={tabsStyles}>
            <span css={tabActiveStyles}>My emails</span>
            <span css={tabInactiveStyles}>Folders</span>
          </div>
          <div css={filtersStyles}>
            <div css={searchSortStatusStyles}>
              <TextButton icon={<IconSearch />}>Search</TextButton>
              <TextButton icon={<IconChevronDown />} iconPosition="right">
                Status
              </TextButton>
            </div>
            <IconToggleGroup>
              <IconToggle icon={<IconSquare />} isActive />
              <IconToggle icon={<IconListView />} />
              <IconToggle icon={<IconCalendar />} />
            </IconToggleGroup>
            <Button variant="accent" icon={<IconPlus />}>
              New email
            </Button>
          </div>
        </div>

        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          hasCloseButton
          cardMaxWidth="440px"
          cardPadding="l2"
          className="grn-context"
          initialFocus={modalFocusRef}
        >
          <div
            ref={modalFocusRef}
            tabIndex={-1}
            css={css`
              outline: none;
            `}
          />
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: ${modalVariant === 'followup' ? 'center' : 'stretch'};
              gap: 24px;
              text-align: ${modalVariant === 'followup' ? 'center' : 'left'};
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 8px;
                width: 100%;
              `}
            >
              <div
                css={css`
                  font-family: 'Flodesk Sans', sans-serif;
                  font-weight: 500;
                  font-size: 24px;
                  line-height: 28.627px;
                  color: var(--grn-color-content);
                `}
              >
                You have 247 unengaged subscribers
              </div>
              <div
                css={css`
                  font-family: 'Flodesk Sans', sans-serif;
                  font-weight: 400;
                  font-size: 14px;
                  line-height: 20px;
                  color: var(--grn-color-content);
                `}
              >
                And sending them emails can hurt your delivery rate. Review them and consider
                archiving these subscribers to keep your sender reputation healthy.
              </div>
            </div>
            {modalVariant === 'initial' ? (
              <div
                css={css`
                  display: flex;
                  justify-content: flex-end;
                  gap: 8px;
                  width: 100%;
                `}
              >
                <Button variant="neutral" onClick={handleRemindLater}>
                  Remind me later
                </Button>
                <Button variant="accent" onClick={handleReview}>
                  Review subscribers
                </Button>
              </div>
            ) : (
              <>
                <div
                  css={css`
                    width: 100%;
                    height: 280px;
                    border-radius: var(--grn-radius-s);
                    background: #dcf2ce;
                  `}
                />
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 16px;
                    width: 100%;
                  `}
                >
                  <Button variant="accent" onClick={handleReview} hasFullWidth>
                    Review unengaged subscribers
                  </Button>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={handleLearnMore}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleLearnMore();
                    }}
                    css={css`
                      font-family: 'Flodesk Sans', sans-serif;
                      font-weight: 400;
                      font-size: 14px;
                      line-height: 20px;
                      color: var(--grn-color-content2);
                      text-decoration: underline;
                      cursor: pointer;
                    `}
                  >
                    Learn more
                  </span>
                </div>
              </>
            )}
          </div>
        </Modal>

        <div css={gridStyles}>
          {EMAILS.map(email => (
            <div key={email.image} css={cardWrapperStyles}>
              <img css={coverStyles} src={email.image} alt={email.title} />
              <div css={cardBodyStyles}>
                <span css={cardTitleStyles}>{email.title}</span>
                <div css={badgeRowStyles}>
                  <StatusBadge status={email.status} meta={email.meta} />
                  {email.status === 'sent' && <IconButton icon={<IconChart />} />}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
